import _Dialog from "@mui/material/Dialog";
import _Typography from "@mui/material/Typography";
import _Close from "@mui/icons-material/Close";
import _Popover from "@mui/material/Popover";
import _Fullscreen from "@mui/icons-material/Fullscreen";
import _PlayArrow from "@mui/icons-material/PlayArrow";
import _Pause from "@mui/icons-material/Pause";
import _VolumeUp from "@mui/icons-material/VolumeUp";
import _VolumeOff from "@mui/icons-material/VolumeOff";
import _Download from "@mui/icons-material/Download";
import _CircularProgress from "@mui/material/CircularProgress";
import _Box from "@mui/material/Box";
import _IconButton from "@mui/material/IconButton";
import _Instagram from "@mui/icons-material/Instagram";
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FacebookIcon, FacebookShareButton, LinkedinIcon, LinkedinShareButton, ThreadsIcon, ThreadsShareButton } from 'react-share';
import { useClipboard } from '../../../hooks/clipboard';
import { useFileSaver } from '../../../hooks/file-saver';
import { useAmplitudeTracker } from '../../../hooks/tracker';
import { ShareIcon } from '../../icons';
const shareIconProps = {
  bgStyle: {
    fill: 'rgba(255,255,255,0.14)'
  },
  iconFillColor: '#fff',
  round: true,
  size: 34
};
const SHARE_ENABLED = false;
const shareButtonStyle = {
  alignItems: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  padding: 0
};
function BfgSummaryVideoCard({
  event,
  videoUrl
}) {
  const {
    t
  } = useTranslation();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const {
    downloadBlob
  } = useFileSaver();
  const [downloading, setDownloading] = useState(false);
  const {
    trackEvent
  } = useAmplitudeTracker();
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const baseVideoRef = useRef(null);
  const {
    copy
  } = useClipboard(() => enqueueSnackbar(t('commons.copied_to_clipboard'), {
    variant: 'success'
  }), () => enqueueSnackbar(t('commons.not_copied_to_clipboard'), {
    variant: 'error'
  }));
  useEffect(() => {
    if (!baseVideoRef.current) return;
    if (expanded) baseVideoRef.current.pause();else if (playing) baseVideoRef.current.play().catch(() => {});
  }, [expanded]);
  function togglePlaying() {
    if (!baseVideoRef.current) return;
    if (baseVideoRef.current.paused) baseVideoRef.current.play().catch(() => {});else baseVideoRef.current.pause();
  }
  async function handleDownload() {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(videoUrl);
      const blob = await res.blob();
      downloadBlob(`${event.title}.mp4`, blob);
      trackEvent('Bfg Summary Video Downloaded', {
        event_id: event.id
      });
    } catch {
      enqueueSnackbar(t('commons.not_downloaded_video'), {
        variant: 'error'
      });
    } finally {
      setDownloading(false);
    }
  }
  function trackShare(network) {
    trackEvent('Bfg Summary Video Shared', {
      event_id: event.id,
      network
    });
  }
  async function handleShare(network = 'native') {
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.title,
          url: videoUrl
        });
        trackShare(network);
      } catch {
        // ignore
      }
    } else {
      copy(videoUrl);
      trackShare('copy_link');
    }
  }
  const shareButtons = /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 1
  }, /*#__PURE__*/React.createElement(FacebookShareButton, {
    hashtag: "#BikeForGood",
    onClick: () => trackShare('facebook'),
    style: shareButtonStyle,
    url: videoUrl
  }, /*#__PURE__*/React.createElement(FacebookIcon, shareIconProps)), /*#__PURE__*/React.createElement(LinkedinShareButton, {
    onClick: () => trackShare('linkedin'),
    source: event.title,
    style: shareButtonStyle,
    title: event.title,
    url: videoUrl
  }, /*#__PURE__*/React.createElement(LinkedinIcon, shareIconProps)), /*#__PURE__*/React.createElement(ThreadsShareButton, {
    onClick: () => trackShare('threads'),
    style: shareButtonStyle,
    title: event.title,
    url: videoUrl
  }, /*#__PURE__*/React.createElement(ThreadsIcon, shareIconProps)), /*#__PURE__*/React.createElement(_IconButton, {
    "aria-label": "Instagram",
    onClick: () => handleShare('instagram'),
    sx: {
      bgcolor: 'rgba(255,255,255,0.14)',
      color: '#fff',
      height: 34,
      width: 34,
      '&:hover': {
        bgcolor: 'rgba(255,255,255,0.24)'
      }
    }
  }, /*#__PURE__*/React.createElement(_Instagram, {
    fontSize: "small"
  })), /*#__PURE__*/React.createElement(_IconButton, {
    "aria-label": t('commons.actions.share'),
    onClick: () => handleShare(),
    sx: {
      bgcolor: 'rgba(255,255,255,0.14)',
      color: '#fff',
      height: 34,
      width: 34,
      '&:hover': {
        bgcolor: 'rgba(255,255,255,0.24)'
      }
    }
  }, /*#__PURE__*/React.createElement(ShareIcon, {
    fontSize: "small"
  })));
  const overlayButtonSx = {
    bgcolor: 'rgba(0,0,0,0.45)',
    color: '#fff',
    '&:hover': {
      bgcolor: 'rgba(0,0,0,0.65)'
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Box, {
    sx: {
      borderRadius: '14px',
      overflow: 'hidden',
      position: 'relative',
      '&:hover .bfg-video-overlay': {
        opacity: 1
      }
    }
  }, /*#__PURE__*/React.createElement(_Box, {
    autoPlay: true,
    loop: true,
    playsInline: true,
    component: "video",
    muted: muted,
    onClick: togglePlaying,
    onPause: () => setPlaying(false),
    onPlay: () => setPlaying(true),
    ref: baseVideoRef,
    src: videoUrl,
    sx: {
      backgroundColor: '#000',
      cursor: 'pointer',
      display: 'block',
      opacity: expanded ? 0 : 1,
      transition: 'opacity 0.2s',
      width: '100%'
    }
  }), /*#__PURE__*/React.createElement(_Box, {
    className: "bfg-video-overlay",
    sx: {
      alignItems: 'flex-start',
      background: 'linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, transparent 25%, transparent 70%, rgba(0,0,0,0.45) 100%)',
      display: 'flex',
      flexDirection: 'column',
      inset: 0,
      justifyContent: 'space-between',
      opacity: 0,
      padding: 1.5,
      pointerEvents: 'none',
      position: 'absolute',
      transition: 'opacity 0.2s'
    }
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    sx: {
      pointerEvents: 'auto'
    },
    width: "100%"
  }, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    gap: 0.75
  }, SHARE_ENABLED && /*#__PURE__*/React.createElement(_IconButton, {
    "aria-label": t('commons.actions.share'),
    onClick: e => setShareAnchorEl(e.currentTarget),
    size: "small",
    sx: overlayButtonSx
  }, /*#__PURE__*/React.createElement(ShareIcon, {
    fontSize: "small"
  })), /*#__PURE__*/React.createElement(_IconButton, {
    "aria-label": t('commons.actions.download'),
    disabled: downloading,
    onClick: handleDownload,
    size: "small",
    sx: overlayButtonSx
  }, downloading ? /*#__PURE__*/React.createElement(_CircularProgress, {
    size: 16,
    sx: {
      color: '#fff'
    }
  }) : /*#__PURE__*/React.createElement(_Download, {
    fontSize: "small"
  }))), /*#__PURE__*/React.createElement(_IconButton, {
    "aria-label": t(muted ? 'commons.actions.unmute' : 'commons.actions.mute'),
    onClick: () => setMuted(current => !current),
    size: "small",
    sx: overlayButtonSx
  }, muted ? /*#__PURE__*/React.createElement(_VolumeOff, {
    fontSize: "small"
  }) : /*#__PURE__*/React.createElement(_VolumeUp, {
    fontSize: "small"
  }))), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    sx: {
      pointerEvents: 'auto'
    },
    width: "100%"
  }, /*#__PURE__*/React.createElement(_IconButton, {
    "aria-label": t(playing ? 'commons.actions.pause' : 'commons.actions.play'),
    onClick: togglePlaying,
    size: "small",
    sx: overlayButtonSx
  }, playing ? /*#__PURE__*/React.createElement(_Pause, {
    fontSize: "small"
  }) : /*#__PURE__*/React.createElement(_PlayArrow, {
    fontSize: "small"
  })), /*#__PURE__*/React.createElement(_IconButton, {
    "aria-label": t('commons.actions.enter_fullscreen'),
    onClick: () => setExpanded(true),
    size: "small",
    sx: overlayButtonSx
  }, /*#__PURE__*/React.createElement(_Fullscreen, {
    fontSize: "small"
  }))))), SHARE_ENABLED && /*#__PURE__*/React.createElement(_Popover, {
    anchorEl: shareAnchorEl,
    anchorOrigin: {
      horizontal: 'center',
      vertical: 'bottom'
    },
    onClose: () => setShareAnchorEl(null),
    open: Boolean(shareAnchorEl),
    slotProps: {
      paper: {
        sx: {
          bgcolor: '#1E2A20',
          borderRadius: '12px',
          marginTop: 1,
          padding: 1
        }
      }
    },
    transformOrigin: {
      horizontal: 'center',
      vertical: 'top'
    }
  }, shareButtons), /*#__PURE__*/React.createElement(_Dialog, {
    maxWidth: "xs",
    onClose: () => setExpanded(false),
    open: expanded,
    slotProps: {
      paper: {
        sx: {
          borderRadius: '20px',
          overflow: 'hidden'
        }
      }
    }
  }, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: 3,
    sx: {
      background: 'linear-gradient(135deg, #16321F 0%, #2C5940 55%, #16321F 100%)',
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement(_IconButton, {
    "aria-label": t('commons.actions.close'),
    onClick: () => setExpanded(false),
    size: "small",
    sx: {
      position: 'absolute',
      right: 12,
      top: 12,
      ...overlayButtonSx
    }
  }, /*#__PURE__*/React.createElement(_Close, {
    fontSize: "small"
  })), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    paddingRight: 5
  }, /*#__PURE__*/React.createElement(_Typography, {
    sx: {
      color: '#fff',
      fontSize: 16,
      fontWeight: 800,
      lineHeight: 1.6
    }
  }, t('commons.challenges.summary_video.title'))), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(_Box, {
    sx: {
      borderRadius: '18px',
      boxShadow: '0 16px 36px rgba(0,0,0,0.45)',
      display: 'inline-flex',
      maxWidth: '100%',
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement(_Box, {
    autoPlay: true,
    loop: true,
    playsInline: true,
    component: "video",
    muted: muted,
    src: videoUrl,
    sx: {
      backgroundColor: '#000',
      display: 'block',
      height: 'auto',
      maxHeight: 480,
      maxWidth: '100%',
      width: 'auto'
    }
  }))), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 1,
    justifyContent: "center"
  }, SHARE_ENABLED && shareButtons, /*#__PURE__*/React.createElement(_IconButton, {
    "aria-label": t('commons.actions.download'),
    disabled: downloading,
    onClick: handleDownload,
    sx: {
      bgcolor: 'rgba(255,255,255,0.14)',
      color: '#fff',
      height: 34,
      width: 34,
      '&:hover': {
        bgcolor: 'rgba(255,255,255,0.24)'
      }
    }
  }, downloading ? /*#__PURE__*/React.createElement(_CircularProgress, {
    size: 18,
    sx: {
      color: '#fff'
    }
  }) : /*#__PURE__*/React.createElement(_Download, {
    fontSize: "small"
  }))), /*#__PURE__*/React.createElement(_Typography, {
    sx: {
      color: 'rgba(255,255,255,0.75)',
      fontSize: 13,
      textAlign: 'center'
    },
    variant: "body2"
  }, t('commons.challenges.summary_video.subtitle')))));
}
export default BfgSummaryVideoCard;